/**
 * Entry application component used to compose providers and render Routes.
 * */
import React from "react";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { toast } from "react-toastify";

import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider,
  split,
  HttpLink,
  ApolloLink,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { WebSocketLink } from "@apollo/client/link/ws";
import { getMainDefinition } from "@apollo/client/utilities";
import { MatomoProvider } from "@datapunt/matomo-tracker-react";
import { PersistGate } from "redux-persist/integration/react";

import { I18nProvider } from "../_metronic/i18n";
import { LayoutSplashScreen, MaterialThemeProvider } from "../_metronic/layout";
import { Routes } from "../app/Routes";

import { MatomoCustomProvider, Instance } from "./MatomoProvider";
import { APP_URL, PIN, TOKEN, WEB_SOCKET_APP_URL } from "./Utils/constant";

import "react-toastify/dist/ReactToastify.css";

toast.configure();

const wsLink = new WebSocketLink({
  uri: WEB_SOCKET_APP_URL,
  options: {
    reconnect: true,
    lazy: true,
  },
});

const httpLink = new HttpLink({
  uri: APP_URL,
  credentials: "include",
});

const link = split(
  // Split based on operation type
  ({ query }) => {
    const definition = getMainDefinition(query);

    return (
      definition.kind === "OperationDefinition" &&
      definition.operation === "subscription"
    );
  },

  wsLink,
  httpLink,
);

const authLink = setContext((_, { headers }) => {
  // Get the authentication token from local storage if it exists
  const token = localStorage.getItem(TOKEN);

  const pin = localStorage.getItem(PIN);

  // Return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      token: token || "",
      pin,
    },
  };
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  // if (graphQLErrors)
  //   graphQLErrors.forEach(({ message, locations, path }) =>
  //     console.log(
  //       `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
  //     ),
  //   );
  // if (networkError) console.log(`[Network error]: ${networkError}`);
});

const client = new ApolloClient({
  // link: authLink.concat(link),
  link: ApolloLink.from([authLink, errorLink, link]),
  cache: new InMemoryCache(),
});

export default function App({ store, persistor, basename }) {
  return (
    /* Provide Matamo tracking; docs: https://www.npmjs.com/package/@datapunt/matomo-tracker-react*/
    <MatomoProvider value={Instance}>
      <MatomoCustomProvider>
        {/* Provide Redux store */}
        <Provider store={store}>
          <ApolloProvider client={client}>
            {/* Asynchronously persist redux stores and show `SplashScreen` while it's loading. */}
            <PersistGate persistor={persistor} loading={<LayoutSplashScreen />}>
              {/* Add high level `Suspense` in case if was not handled inside the React tree. */}
              <React.Suspense fallback={<LayoutSplashScreen />}>
                {/* Override `basename` (e.g: `homepage` in `package.json`) */}
                <BrowserRouter basename={basename}>
                  {/*This library only returns the location that has been active before the recent location change in the current window lifetime.*/}
                  <MaterialThemeProvider>
                    {/* Provide `react-intl` context synchronized with Redux state.  */}
                    <I18nProvider>
                      {/* Render routes with provided `Layout`. */}
                      <Routes />
                    </I18nProvider>
                  </MaterialThemeProvider>
                </BrowserRouter>
              </React.Suspense>
            </PersistGate>
          </ApolloProvider>
        </Provider>
      </MatomoCustomProvider>
    </MatomoProvider>
  );
}
